import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

const navLinks = [
  { label: 'Home', path: '/' },
  { label: 'About', path: '/about' },
  { label: 'Rooms', path: '/rooms' },
  { label: 'Amenities', path: '/amenities' },
  { label: 'Gallery', path: '/gallery' },
  { label: 'Blog', path: '/blog' },
  { label: 'Reviews', path: '/reviews' },
  { label: 'Contact', path: '/contact' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === '/';

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setMenuOpen(false); }, [location]);

  const isTransparent = isHome && !scrolled;

  return (
    <>
      <nav style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1000,
        background: scrolled ? 'rgba(26, 46, 26, 0.92)' : 'rgba(26, 46, 26, 0.85)',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        borderBottom: '1px solid rgba(201,168,76,0.15)',
        transition: 'all 0.5s ease',
        padding: scrolled ? '8px 0' : '12px 0',
      }}>
        <div className="container" style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between'
        }}>
          {/* Logo */}
          <Link to="/" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center' }}>
            <img
              src="/logo-green.jpeg"
              alt="Villa Tesori Logo"
              style={{
                height: scrolled ? '42px' : '52px',
                width: 'auto',
                transition: 'all 0.3s ease',
              }}
            />
          </Link>

          {/* Desktop Links */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }} className="desktop-nav">
            {navLinks.map(link => (
              <Link key={link.path} to={link.path} style={{
                fontFamily: 'Jost, sans-serif',
                fontSize: '0.7rem',
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                color: location.pathname === link.path ? 'var(--gold-light)' : 'rgba(245,240,232,0.75)',
                padding: '8px 10px',
                borderBottom: location.pathname === link.path ? '1px solid var(--gold)' : '1px solid transparent',
                transition: 'all 0.3s',
                fontWeight: 400,
              }}
                onMouseEnter={e => { if (location.pathname !== link.path) e.target.style.color = 'var(--gold-light)'; }}
                onMouseLeave={e => { if (location.pathname !== link.path) e.target.style.color = 'rgba(245,240,232,0.75)'; }}
              >{link.label}</Link>
            ))}
          </div>

          {/* CTA & Hamburger Wrapper */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <a href="https://wa.me/917558806677" target="_blank" rel="noopener noreferrer" className="btn btn-gold nav-cta" style={{
              padding: '8px 18px', 
              fontSize: '0.68rem', 
              letterSpacing: '0.12em', 
              textDecoration: 'none',
              borderRadius: '2px',
              whiteSpace: 'nowrap'
            }}>
              Book Now
            </a>

            {/* Hamburger */}
            <button onClick={() => setMenuOpen(!menuOpen)} style={{
              background: 'none', border: 'none', cursor: 'pointer',
              display: 'none', flexDirection: 'column', gap: 5, padding: 6,
              marginLeft: '2px'
            }} className="mobile-menu-btn" aria-label="Menu">
              {[0, 1, 2].map(i => (
                <span key={i} style={{
                  display: 'block', width: 22, height: 1.5,
                  background: 'var(--gold-light)',
                  transition: 'all 0.3s',
                  transformOrigin: 'center',
                  transform: menuOpen
                    ? i === 0 ? 'rotate(45deg) translate(4.5px, 4.5px)' : i === 1 ? 'scaleX(0)' : 'rotate(-45deg) translate(4.5px, -4.5px)'
                    : 'none',
                  opacity: menuOpen && i === 1 ? 0 : 1,
                }} />
              ))}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0, x: '100%' }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            style={{
              position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
              background: 'var(--forest)', zIndex: 1100,
              display: 'flex', flexDirection: 'column', alignItems: 'center',
              justifyContent: 'flex-start', gap: 10, padding: '80px 40px 40px',
              overflowY: 'auto'
            }}
          >
            <button 
              onClick={() => setMenuOpen(false)}
              style={{
                position: 'absolute', top: 20, right: 20,
                background: 'rgba(201,168,76,0.1)', border: 'none', color: 'var(--gold-light)',
                fontSize: '2rem', cursor: 'pointer', width: 44, height: 44, 
                borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                lineHeight: 1
              }}
            >
              ×
            </button>
            
            <img
              src="/logo-green.jpeg"
              alt="Villa Tesori Logo"
              style={{
                height: '70px',
                width: 'auto',
                marginBottom: 30
              }}
            />
            {navLinks.map((link, i) => (
              <motion.div
                key={link.path}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
                style={{ width: '100%' }}
              >
                <Link to={link.path} style={{
                  fontFamily: 'Cormorant Garamond, serif',
                  fontSize: '1.6rem', fontWeight: 300,
                  color: location.pathname === link.path ? 'var(--gold-light)' : 'var(--mist)',
                  display: 'block', textAlign: 'center', padding: '10px 0',
                  borderBottom: '1px solid rgba(201,168,76,0.05)'
                }}>{link.label}</Link>
              </motion.div>
            ))}
            <a href="https://wa.me/917558806677" target="_blank" rel="noopener noreferrer" className="btn btn-gold" style={{ marginTop: 30, width: '100%', justifyContent: 'center', textDecoration: 'none' }}>
              Book Your Stay
            </a>
          </motion.div>
        )}
      </AnimatePresence>

      <style>{`
        @media (max-width: 1024px) {
          .desktop-nav { display: none !important; }
          .mobile-menu-btn { display: flex !important; }
        }
        
        @media (max-width: 768px) {
          .nav-cta {
            padding: 7px 12px !important;
            font-size: 0.62rem !important;
            letter-spacing: 0.1em !important;
          }
          nav img {
            height: 38px !important;
          }
        }

        @media (max-width: 400px) {
          .nav-cta {
            display: none !important;
          }
        }
      `}</style>
    </>
  );
}
